import { Item } from '@react-stately/collections'
import { Button } from 'blaze-ui/Button'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { ChevronDown, EyeOpen } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { Menu } from 'components/Menu/Menu'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { AccountAvatar } from 'components/PostPreviewModalV2/components/AccountToggle/AccountAvatar'
import { extractProfileInfo, formatHandle } from 'components/ExternalIntegrationSelect/utils'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { ScheduledPostTarget } from 'types'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import Styles from './ViewPostMenu.module.scss'

export interface PostedAccount {
  target: ScheduledPostTarget
  integration: ExternalIntegration
}

interface ViewPostMenuProps {
  accounts: PostedAccount[]
}

export const ViewPostMenu = ({ accounts }: ViewPostMenuProps) => {
  const openWindowSafely = useOpenWindowSafely()

  if (accounts.length === 1) {
    const url = accounts[0].target.post_url
    if (!url) return null
    return (
      <ButtonLink
        to={url}
        target="_blank"
        size="md"
        variant="secondary"
        fullWidth
        frontIcon={EyeOpen}
        className={Styles.trigger}
      >
        View Post
      </ButtonLink>
    )
  }

  const handleAction = (key: string | number) => {
    const account = accounts.find((a) => a.target.id.toString() === key.toString())
    if (account?.target.post_url) {
      openWindowSafely(account.target.post_url)
    }
  }

  return (
    <MenuTrigger>
      <Button
        variant="secondary"
        size="md"
        fullWidth
        frontIcon={EyeOpen}
        endIcon={ChevronDown}
        className={Styles.trigger}
      >
        View Post
      </Button>
      <Menu aria-label="View posted accounts" onAction={handleAction} className={Styles.menu}>
        {accounts.map(({ target, integration }) => {
          const { name, username, email } = extractProfileInfo(integration)
          const handle = formatHandle(username, email)
          return (
            <Item key={target.id} textValue={name}>
              <div className={Styles.row}>
                <AccountAvatar account={integration} />
                <div className={Styles.details}>
                  <Text variant="smallList" color="var(--dark-90)">
                    {name}
                  </Text>
                  {handle && (
                    <Text variant="metadata" color="var(--dark-40)">
                      {handle}
                    </Text>
                  )}
                </div>
              </div>
            </Item>
          )
        })}
      </Menu>
    </MenuTrigger>
  )
}
